import { Box } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

import { useTranslation } from 'react-i18next';

import { CheckOutlineIcon, ChevronRightIcon, HandByeIcon } from 'Asset/icon';
import { Condition } from 'Component/others';
import checkPublicRoute from 'Helper/checkPublicRoute';
import { TextMedium, TextSemiBold } from 'UiKit/typography';
import useGetHelpRequestStatus from 'Domain/host-help-partner/useGetHelpRequestStatus';

import styles from './styles.module.css';

const HelpHostWidget = () => {
  const { t } = useTranslation('host-help-partner');
  const navigate = useNavigate();

  const pathname = useLocation().pathname;
  const isPublic = checkPublicRoute(pathname);

  const { data: dataStatusHelp, isPending: isLoadingHelpStatus } = useGetHelpRequestStatus({ isPublic });

  const isRequestingHelpHost = dataStatusHelp?.mappedStatus === 'SEARCHING' || dataStatusHelp?.status === 'NO_ACTIVE_OPERATORS';
  const isAssignedHelpHost = dataStatusHelp?.mappedStatus === 'ASSIGNED';
  const isOperatorExist = dataStatusHelp?.activeOperators?.length > 0;

  const progressText = isOperatorExist ? t('help_host_widget.progress_text') : t('help_host_widget.progress_no_operator_text');

  const totalDuration = 3 * 60;
  const cancelDuration = dataStatusHelp?.cancelDuration ?? 0;
  const initialWidth = (cancelDuration / totalDuration) * 100;
  const remainingDuration = totalDuration - cancelDuration;

  if (isLoadingHelpStatus || (!isAssignedHelpHost && !isRequestingHelpHost)) return null;

  return (
    <Box className={styles['help-host-widget__container']} onClick={() => navigate('/host/help-request-form')}>
      <Box className={styles['help-host-widget__content']}>
        <Condition
          statement={isAssignedHelpHost}
          fallback={
            <Condition statement={isRequestingHelpHost} fallback={null}>
              <Box display="flex" alignItems="center" gap="12px">
                <HandByeIcon color="var(--color-primary50)" />
                <Box className={styles['help-host-widget__text']}>
                  <TextSemiBold fontSize="var(--font-body3)">{progressText}</TextSemiBold>
                  <TextMedium fontSize="var(--font-caption1)" color="var(--color-neutral60)">
                    {t('help_host_widget.subtitle')}
                  </TextMedium>
                </Box>
              </Box>
            </Condition>
          }
        >
          <Box display="flex" alignItems="center" gap="12px">
            <CheckOutlineIcon size="22px" color="var(--color-success50)" />
            <Box className={styles['help-host-widget__text']}>
              <TextSemiBold fontSize="var(--font-body3)">
                {dataStatusHelp?.operatorName} {t('help_host_widget.success_text')}
              </TextSemiBold>
              <TextMedium fontSize="var(--font-caption1)" color="var(--color-neutral60)">
                {t('help_host_widget.subtitle')}
              </TextMedium>
            </Box>
          </Box>
        </Condition>
      </Box>
      <ChevronRightIcon size={20} color="var(--color-semantic-text)" />
      <Condition statement={isRequestingHelpHost}>
        <Box className={styles['help-host-widget__loading-track']}>
          <motion.div
            className={styles['help-host-widget__loading-fill']}
            initial={{ width: `${initialWidth}%` }}
            animate={{ width: '100%' }}
            transition={{ duration: remainingDuration, ease: 'linear' }}
          />
        </Box>
      </Condition>
    </Box>
  );
};

export default HelpHostWidget;
